// Types for the Production Manager dashboard
export type ChecklistStage =
  | "checkin"
  | "depollution"
  | "stage1"
  | "stage2"
  | "stage3"
  | "stage4";

export type ChecklistItemStatus = "removed" | "damaged" | "current" | "todo";

export interface ChecklistItem {
  id: string;
  label: string;
  status: ChecklistItemStatus;
}

export interface Vehicle {
  id: string;
  name: string;
  registration: string;
  colour: string;
  reference: string;
  imageUrl?: string;
  checklist?: ChecklistItem[];
}

export type ColumnStatus = "ok" | "attention" | "idle";

export interface BoardColumn {
  id: string;
  title: string;
  status: ColumnStatus;
  stage: ChecklistStage;
  vehicles: Vehicle[];
}

export interface KpiMetric {
  id: string;
  label: string;
  value: string;
}

export interface ProductionDashboardData {
  title: string;
  kpis: KpiMetric[];
  stationAverages: string[];
  materials: { name: string; value: string }[];
  columns: BoardColumn[];
}

// Helpers
export const isAttentionStatus = (status: ChecklistItemStatus) =>
  status === "damaged" || status === "todo";

export const isOkStatus = (status: ChecklistItemStatus) =>
  status === "removed" || status === "current";

// Mocked Data
export const productionDashboardData: ProductionDashboardData = {
  title: "Production Manager",
  kpis: [
    { id: "available", label: "Awaiting Checkin", value: "32" },
    { id: "avgProcessed", label: "Average Processed", value: "76%" },
    { id: "avgDepollution", label: "Average De-pollution Time (Mins)", value: "18" },
  ],
  stationAverages: ["Station 1 : 00:32", "Station 2 : 00:22", "Station 3 : 00:29", "Station 4 : 00:45"],
  materials: [
    { name: "Steel", value: "120kg" },
    { name: "Aluminium", value: "50kg" },
    { name: "Copper", value: "8.5kg" },
    { name: "Plastic", value: "15kg" },
  ],
  columns: [
    {
      id: "checkin",
      title: "Check-In",
      status: "idle",
      stage: "checkin",
      vehicles: [
        
        {
          id: "v-ck-2",
          name: "Ford Fiesta Zetec",
          registration: "XY34 ZZZ",
          colour: "Red",
          reference: "REF-10441",
          imageUrl: "https://cdn.recyclingsoftware.co.uk/public/images/vehicle/6d25ced2-44af-489e-17d6-08de2774aee7",
        },
        {
          id: "v-ck-1",
          name: "Peugeot 208",
          registration: "AB12 CDE",
          colour: "Blue",
          reference: "REF-10293",
          imageUrl: "https://cdn.recyclingsoftware.co.uk/public/images/vehicle/46001838-7228-483f-923a-08de2ac5ee2a",
        }
      ],
    },
    {
      id: "depollution",
      title: "Depollution",
      status: "ok",
      stage: "depollution",
      vehicles: [
        {
          id: "v-dp-1",
          name: "VW Golf TSI",
          registration: "GK19 PLO",
          colour: "Black",
          reference: "REF-22014",
          imageUrl: "https://cdn.recyclingsoftware.co.uk/public/images/vehicle/e9393010-705e-4898-128e-08de203bdee1",
          checklist: [
            { id: "battery", label: "Battery", status: "removed" },
            { id: "airbag", label: "Air bag", status: "removed" },
            { id: "fuel", label: "Fuel", status: "todo" },
            { id: "brakeFluid", label: "Brake Fluid", status: "current" },
            { id: "coolant", label: "Coolant", status: "todo" },
          ],
        },
      ],
    },
    {
      id: "stage1",
      title: "Stage 1",
      status: "attention",
      stage: "stage1",
      vehicles: [
        {
          id: "v-s1-1",
          name: "Peugeot 208",
          registration: "AB12 CDE",
          imageUrl: "https://cdn.recyclingsoftware.co.uk/public/images/vehicle/46001838-7228-483f-923a-08de2ac5ee2a",
          colour: "Blue",
          reference: "REF-10293",
          checklist: [
            { id: "fd-lh", label: "Front Door LH - Removed", status: "removed" },
            { id: "fd-rh", label: "Front Door RH - Removed", status: "removed" },
            { id: "rd-lh", label: "Rear Door LH - Damaged", status: "damaged" },
            { id: "rd-rh", label: "Rear Door RH - Removed", status: "removed" },
            { id: "brake", label: "Brake Fluid - Current", status: "current" },
            { id: "cool", label: "Coolant - To do", status: "todo" },
          ],
        },
      ],
    },
  
    {
      id: "stage2",
      title: "Stage 2",
      status: "ok",
      stage: "stage2",
      vehicles: [
        {
          id: "v-s2-1",
          name: "Ford Fiesta Zetec",
          registration: "XY34 ZZZ",
          colour: "Red",
          reference: "REF-10441",
          imageUrl: "https://cdn.recyclingsoftware.co.uk/public/images/vehicle/6d25ced2-44af-489e-17d6-08de2774aee7",
          checklist: [
            { id: "seats", label: "Seats - Removed", status: "removed" },
            { id: "carpets", label: "Carpets - Current", status: "current" },
            { id: "trims", label: "Trims - To do", status: "todo" },
          ],
        },
      ],
    },
    {
      id: "stage3",
      title: "Stage 3",
      status: "attention",
      stage: "stage3",
      vehicles: [ {
          id: "v-s3-1",
          name: "BMW 1 Series",
          registration: "BM11 WMM",
          imageUrl: "https://cdn.recyclingsoftware.co.uk/public/images/vehicle/8ef33bc0-b543-43ba-7327-08de0b083c38",
          colour: "White",
          reference: "REF-77820",
          checklist: [
// * Catalytic Converter - Removed
  // * Exhaust - Damaged
  // * Alternator - Removed
  // * Fuel Tank -  Current
  // * ABS Pump - to do
            { id: "catalyticConverter", label: "Catalytic Converter", status: "removed" },
            { id: "exhaust", label: "Exhaust", status: "damaged" },
            { id: "alternator", label: "Alternator", status: "removed" },
            { id: "fuelTank", label: "Fuel Tank", status: "current" },
            { id: "absPump", label: "ABS Pump", status: "todo" },
          ],
        },
      ],
    },
    {
      id: "stage4",
      title: "Stage 4",
      status: "ok",
      stage: "stage4",
      vehicles: [
        {
          id: "v-s4-1",
          name: "Audi A3 TFSI",
          registration: "AU20 DDD",
          colour: "Grey",
          reference: "REF-45001",
          imageUrl: "https://cdn.recyclingsoftware.co.uk/public/images/vehicle/b0efb347-c48a-42bd-39ed-08de28177191",
          checklist: [
            { id: "engine", label: "Engine - Removed", status: "removed" },
            { id: "loom", label: "Loom - Current", status: "current" },
            { id: "wheels", label: "Wheels - To do", status: "todo" },
          ],
        },
      ],
    },
  ],
};


